import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUpload, FiDownload, FiSettings, FiZap, FiInfo, FiCheck, FiX, FiLayers, FiTrash2, FiArchive } = FiIcons;

function BatchImageCompressor() {
  const [selectedImages, setSelectedImages] = useState([]);
  const [compressedImages, setCompressedImages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [compressionLevel, setCompressionLevel] = useState(80);
  const [outputFormat, setOutputFormat] = useState('jpeg');
  const [downloadOption, setDownloadOption] = useState('zip');
  const [totalOriginalSize, setTotalOriginalSize] = useState(0);
  const [totalCompressedSize, setTotalCompressedSize] = useState(0);

  const handleImageSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      const imagesWithId = imageFiles.map((file, index) => ({
        id: Date.now() + index,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file),
        status: 'pending'
      }));
      
      setSelectedImages(prev => [...prev, ...imagesWithId]);
      
      const totalSize = imagesWithId.reduce((sum, img) => sum + img.size, 0);
      setTotalOriginalSize(prev => prev + totalSize);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      const imagesWithId = imageFiles.map((file, index) => ({
        id: Date.now() + index,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file),
        status: 'pending'
      }));
      
      setSelectedImages(prev => [...prev, ...imagesWithId]);
      
      const totalSize = imagesWithId.reduce((sum, img) => sum + img.size, 0);
      setTotalOriginalSize(prev => prev + totalSize);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const removeImage = (id) => {
    const imageToRemove = selectedImages.find(img => img.id === id);
    if (imageToRemove) {
      setSelectedImages(prev => prev.filter(img => img.id !== id));
      setTotalOriginalSize(prev => prev - imageToRemove.size);
      URL.revokeObjectURL(imageToRemove.preview);
    }
  };

  const compressImage = (imageData) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height);

        const quality = compressionLevel / 100;
        const mimeType = outputFormat === 'png' ? 'image/png' : 
                        outputFormat === 'webp' ? 'image/webp' : 'image/jpeg';

        canvas.toBlob((blob) => {
          resolve({
            ...imageData,
            compressedBlob: blob,
            compressedSize: blob.size,
            status: 'completed'
          });
        }, mimeType, quality);
      };

      img.src = imageData.preview;
    });
  };

  const compressAllImages = async () => {
    if (selectedImages.length === 0) return;

    setIsProcessing(true);
    setProcessingProgress(0);
    const results = [];
    let totalCompressed = 0;

    for (let i = 0; i < selectedImages.length; i++) {
      const image = selectedImages[i];
      
      // Update status to processing
      setSelectedImages(prev => 
        prev.map(img => 
          img.id === image.id ? { ...img, status: 'processing' } : img
        )
      );

      try {
        const compressedImage = await compressImage(image);
        results.push(compressedImage);
        totalCompressed += compressedImage.compressedSize;

        // Update status to completed
        setSelectedImages(prev => 
          prev.map(img => 
            img.id === image.id ? { ...img, status: 'completed' } : img
          )
        );
      } catch (error) {
        console.error(`Failed to compress ${image.name}:`, error);
        setSelectedImages(prev => 
          prev.map(img => 
            img.id === image.id ? { ...img, status: 'error' } : img
          )
        );
      }

      setProcessingProgress(((i + 1) / selectedImages.length) * 100);
    }

    setCompressedImages(results);
    setTotalCompressedSize(totalCompressed);
    setIsProcessing(false);

    // Track batch compression analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'batch_images_compressed', {
        event_category: 'Image Tools',
        event_label: 'Batch Image Compression',
        value: results.length
      });
    }
  };

  const downloadAllAsZip = async () => {
    if (compressedImages.length === 0) return;

    const zip = new JSZip();
    
    compressedImages.forEach((image, index) => {
      if (image.compressedBlob) {
        const extension = outputFormat === 'png' ? 'png' : 
                         outputFormat === 'webp' ? 'webp' : 'jpg';
        const filename = `compressed_${image.name.split('.')[0]}.${extension}`;
        zip.file(filename, image.compressedBlob);
      }
    });

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `compressed_images_${Date.now()}.zip`);

    // Track download analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'batch_compressed_downloaded_zip', {
        event_category: 'Image Tools',
        event_label: 'Batch Compression ZIP Download',
        value: compressedImages.length
      });
    }
  };

  const downloadIndividual = (image) => {
    if (!image.compressedBlob) return;

    const url = URL.createObjectURL(image.compressedBlob);
    const a = document.createElement('a');
    a.href = url;
    const extension = outputFormat === 'png' ? 'png' : 
                     outputFormat === 'webp' ? 'webp' : 'jpg';
    a.download = `compressed_${image.name.split('.')[0]}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resetTool = () => {
    selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
    setSelectedImages([]);
    setCompressedImages([]);
    setTotalOriginalSize(0);
    setTotalCompressedSize(0);
    setProcessingProgress(0);
    setCompressionLevel(80);
    setOutputFormat('jpeg');
    setDownloadOption('zip');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const totalCompressionRatio = totalOriginalSize > 0 ? 
    Math.round(((totalOriginalSize - totalCompressedSize) / totalOriginalSize) * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiLayers} className="text-green-600 text-2xl" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Batch Image Compressor</h2>
          <p className="text-gray-600">Compress multiple images simultaneously to reduce file sizes</p>
        </div>

        {selectedImages.length === 0 ? (
          // Upload Area
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-green-500 transition-colors cursor-pointer"
          >
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className="hidden"
              id="batch-image-upload"
            />
            <label htmlFor="batch-image-upload" className="cursor-pointer">
              <SafeIcon icon={FiUpload} className="text-4xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Select Multiple Images to Compress
              </h3>
              <p className="text-gray-500 mb-4">
                Drag and drop images here, or click to select multiple files
              </p>
              <div className="text-sm text-gray-400">
                Supports: JPEG, PNG, WebP, GIF • Max: 100 images, 50MB each
              </div>
            </label>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Compression Settings */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiSettings} className="mr-2" />
                Batch Compression Settings
              </h3>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quality Level: {compressionLevel}%
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    value={compressionLevel}
                    onChange={(e) => setCompressionLevel(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Higher Compression</span>
                    <span>Better Quality</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Output Format
                  </label>
                  <select
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="jpeg">JPEG (Best compression)</option>
                    <option value="png">PNG (Lossless)</option>
                    <option value="webp">WebP (Modern format)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Download Option
                  </label>
                  <select
                    value={downloadOption}
                    onChange={(e) => setDownloadOption(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="zip">ZIP Archive (All at once)</option>
                    <option value="individual">Individual Downloads</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <SafeIcon icon={FiInfo} className="text-blue-600 mt-0.5 mr-2" />
                  <div className="text-sm text-blue-800">
                    <strong>Batch Processing:</strong> All images will be compressed with the same settings. 
                    Quality level affects file size - lower values create smaller files but may reduce image quality.
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Images Grid */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Selected Images ({selectedImages.length})
                </h3>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                    id="add-more-images"
                  />
                  <label
                    htmlFor="add-more-images"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm flex items-center"
                  >
                    <SafeIcon icon={FiUpload} className="mr-1" />
                    Add More
                  </label>
                  <button
                    onClick={resetTool}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center"
                  >
                    <SafeIcon icon={FiTrash2} className="mr-1" />
                    Clear All
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
                {selectedImages.map((image) => (
                  <div key={image.id} className="relative bg-gray-50 rounded-lg p-3">
                    <img
                      src={image.preview}
                      alt={image.name}
                      className="w-full h-24 object-cover rounded-lg mb-2"
                    />
                    <div className="text-xs text-gray-600">
                      <div className="truncate font-medium">{image.name}</div>
                      <div>{formatFileSize(image.size)}</div>
                      <div className="flex items-center mt-1">
                        {image.status === 'pending' && (
                          <span className="text-gray-500">Pending</span>
                        )}
                        {image.status === 'processing' && (
                          <span className="text-blue-600 flex items-center">
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600 mr-1"></div>
                            Processing
                          </span>
                        )}
                        {image.status === 'completed' && (
                          <span className="text-green-600 flex items-center">
                            <SafeIcon icon={FiCheck} className="mr-1" />
                            Done
                          </span>
                        )}
                        {image.status === 'error' && (
                          <span className="text-red-600 flex items-center">
                            <SafeIcon icon={FiX} className="mr-1" />
                            Error
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeImage(image.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="text-xs" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Processing Progress */}
            {isProcessing && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-blue-800">
                    Compressing Images...
                  </h3>
                  <span className="text-blue-600 font-medium">
                    {Math.round(processingProgress)}%
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${processingProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={compressAllImages}
                disabled={isProcessing || selectedImages.length === 0}
                className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Compressing {selectedImages.length} images...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiZap} className="mr-2" />
                    Compress All Images ({selectedImages.length})
                  </>
                )}
              </motion.button>
            </div>

            {/* Results */}
            {compressedImages.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border border-green-200 rounded-xl p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-green-800 flex items-center">
                    <SafeIcon icon={FiCheck} className="mr-2" />
                    Batch Compression Complete!
                  </h3>
                  <div className="text-sm text-green-600">
                    {compressedImages.length} images processed
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{compressedImages.length}</div>
                    <div className="text-sm text-gray-600">Images Compressed</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{formatFileSize(totalOriginalSize)}</div>
                    <div className="text-sm text-gray-600">Original Size</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{formatFileSize(totalCompressedSize)}</div>
                    <div className="text-sm text-gray-600">Compressed Size</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{totalCompressionRatio}%</div>
                    <div className="text-sm text-gray-600">Space Saved</div>
                  </div>
                </div>

                {/* Download Options */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {downloadOption === 'zip' ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={downloadAllAsZip}
                      className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center"
                    >
                      <SafeIcon icon={FiArchive} className="mr-2" />
                      Download All as ZIP ({formatFileSize(totalCompressedSize)})
                    </motion.button>
                  ) : (
                    <div className="text-center">
                      <p className="text-gray-600 mb-4">Click individual download buttons below:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                        {compressedImages.map((image) => (
                          <button
                            key={image.id}
                            onClick={() => downloadIndividual(image)}
                            className="bg-green-600 text-white px-3 py-2 rounded text-xs hover:bg-green-700 transition-colors flex items-center justify-center"
                          >
                            <SafeIcon icon={FiDownload} className="mr-1" />
                            {image.name.substring(0, 10)}...
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default BatchImageCompressor;